import {
  useMutation,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  useNavigate,
} from "@tanstack/react-router";
import { useCallback, useState } from "react";

import type { ConversationId } from "@speech-agent/common";

import { useTRPC } from "@/infrastructure/trpc/react";

export const Route = createFileRoute(
  "/app/speech-agent/historie/$id"
)({
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.prefetchQuery(
        context.trpc.speechAgent.conversations.get.queryOptions(
          {
            id: params.id as ConversationId,
          }
        )
      ),
      context.queryClient.prefetchQuery(
        context.trpc.speechAgent.config.get.queryOptions()
      ),
    ]);
  },
  component: DetailPage,
});

const formatDate = (d: Date): string => {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
};

function DetailPage() {
  const { id: rawId } = Route.useParams();
  const id = rawId as ConversationId;
  const trpc = useTRPC();
  const navigate = useNavigate();

  const { data: convo, refetch } = useSuspenseQuery(
    trpc.speechAgent.conversations.get.queryOptions({ id })
  );

  const { data: config } = useSuspenseQuery(
    trpc.speechAgent.config.get.queryOptions()
  );

  const [editMode, setEditMode] = useState(false);
  const [editedReport, setEditedReport] = useState(
    convo.reportMd
  );
  const [tagInput, setTagInput] = useState("");
  const [statusMsg, setStatusMsg] = useState("");

  const updateReport = useMutation(
    trpc.speechAgent.conversations.updateReport.mutationOptions(
      {
        onSuccess: () => {
          setEditMode(false);
          setStatusMsg("Uloženo.");
          void refetch();
        },
        onError: (e) => setStatusMsg(`Chyba: ${e.message}`),
      }
    )
  );

  const updateTags = useMutation(
    trpc.speechAgent.conversations.updateTags.mutationOptions(
      {
        onSuccess: () => {
          setTagInput("");
          void refetch();
        },
      }
    )
  );

  const deleteMutation = useMutation(
    trpc.speechAgent.conversations.delete.mutationOptions({
      onSuccess: () => {
        void navigate({ to: "/app/speech-agent/historie" });
      },
    })
  );

  const uploadToSharePoint = useMutation(
    trpc.speechAgent.conversations.uploadToSharePoint.mutationOptions(
      {
        onSuccess: (r) => {
          setStatusMsg(
            r.webUrl
              ? `Nahráno do SharePointu: ${r.filename}`
              : "Nahráno do SharePointu."
          );
          void refetch();
        },
        onError: (e) =>
          setStatusMsg(`Chyba nahrání: ${e.message}`),
      }
    )
  );

  const addTag = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter") return;
      const t = tagInput.trim().toLowerCase();
      if (!t) return;
      updateTags.mutate({ id, tags: [...convo.tags, t] });
    },
    [convo.tags, id, tagInput, updateTags]
  );

  const removeTag = useCallback(
    (t: string) => {
      updateTags.mutate({
        id,
        tags: convo.tags.filter((x) => x !== t),
      });
    },
    [convo.tags, id, updateTags]
  );

  const handleDelete = useCallback(() => {
    if (
      !window.confirm(
        "Opravdu chceš tuto konverzaci smazat? Tato akce je nevratná."
      )
    ) {
      return;
    }
    deleteMutation.mutate({ id });
  }, [deleteMutation, id]);

  const meta = [
    formatDate(new Date(convo.startedAt)),
    convo.agentId,
    convo.status === "finalized"
      ? "dokončeno"
      : "rozpracováno",
    convo.uploadedAt
      ? `nahráno ${formatDate(new Date(convo.uploadedAt))}`
      : null,
    typeof convo.usage.costUsdEstimate === "number"
      ? `~$${convo.usage.costUsdEstimate.toFixed(4)}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section className="page" id="page-detail">
      <div className="card">
        <Link
          to="/app/speech-agent/historie"
          className="card-back"
        >
          ← Zpět na historii
        </Link>
        <div className="card-head">
          <p className="kicker">— Zápis</p>
          <h2 className="card-title">
            {convo.title ?? "(bez názvu)"}
          </h2>
        </div>
        <p className="meta-line">{meta}</p>

        <div className="tag-editor">
          <p className="kicker">— Štítky</p>
          <div className="tag-chip-row">
            {convo.tags.map((t) => (
              <button
                key={t}
                type="button"
                className="tag-chip removable"
                onClick={() => removeTag(t)}
                title="Odebrat štítek"
              >
                {t} ×
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Přidat štítek + Enter (např. autoMax, q2-review)…"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={addTag}
            autoComplete="off"
          />
        </div>

        {statusMsg && <p className="status">{statusMsg}</p>}
        {convo.uploadError && (
          <p className="status error">
            Poslední chyba nahrání: {convo.uploadError}
          </p>
        )}

        <div className="report-edit-bar">
          <button
            type="button"
            className="ghost-btn report-edit-btn"
            onClick={() => {
              if (editMode) {
                setEditMode(false);
                setEditedReport(convo.reportMd);
              } else {
                setEditedReport(convo.reportMd);
                setEditMode(true);
              }
            }}
            disabled={convo.status !== "finalized"}
          >
            {editMode ? "Zrušit úpravy" : "Upravit zápis"}
          </button>
          {editMode && (
            <span className="report-edit-hint">
              Změny se uloží do tohoto zápisu — PDF / .md
              vychází z aktuální verze.
            </span>
          )}
        </div>

        {editMode ? (
          <textarea
            className="report-edit"
            value={editedReport}
            onChange={(e) =>
              setEditedReport(e.target.value)
            }
            spellCheck
          />
        ) : (
          <pre className="report">{convo.reportMd}</pre>
        )}

        <details className="raw-details">
          <summary>Přepis konverzace</summary>
          <pre className="raw">{convo.transcript}</pre>
        </details>

        <div className="card-actions card-actions-bottom">
          {editMode && (
            <button
              type="button"
              className="primary-btn"
              onClick={() =>
                updateReport.mutate({
                  id,
                  reportMd: editedReport,
                })
              }
              disabled={updateReport.isPending}
            >
              {updateReport.isPending
                ? "Ukládám…"
                : "Uložit"}
            </button>
          )}
          <button
            type="button"
            className="ghost-btn"
            onClick={() => {
              window.location.href = `/api/conversations/${id}/md`;
            }}
            disabled={convo.status !== "finalized"}
          >
            Stáhnout .md
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={() => {
              window.location.href = `/api/conversations/${id}/pdf`;
            }}
            disabled={convo.status !== "finalized"}
          >
            Stáhnout PDF
          </button>
          {config.sharepointEnabled && (
            <button
              type="button"
              className="ghost-btn"
              onClick={() =>
                uploadToSharePoint.mutate({ id })
              }
              disabled={uploadToSharePoint.isPending}
            >
              {uploadToSharePoint.isPending
                ? "Nahrávám…"
                : "Nahrát do SharePointu"}
            </button>
          )}
          {convo.sharepointWebUrl && (
            <button
              type="button"
              className="ghost-btn"
              onClick={() => {
                window.open(
                  convo.sharepointWebUrl ?? "",
                  "_blank",
                  "noreferrer"
                );
              }}
            >
              Otevřít v SharePointu ↗
            </button>
          )}
          <button
            type="button"
            className="danger-btn"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            Smazat
          </button>
        </div>
      </div>
    </section>
  );
}
